/**
 * AWS Terrain Tiles (Terrarium PNG) — the global fallback tier.
 *
 * Plain XYZ tiles, no auth, `Access-Control-Allow-Origin: *`. Elevation is
 * packed into RGB as `(R·256 + G + B/256) − 32768` metres.
 */

import type { DemProvider, DemRequest, HeightGrid } from '../types';
import { NoCoverageError, REGIONS } from '../types';
import {
  gridToLonLat,
  latToTileY,
  lonToTileX,
  zoomForResolution,
  type MetricDomain,
} from '../projection';

const TILE = 256;
const MAX_ZOOM = 15;
const MAX_TILES = 144;
const URL_BASE = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium';

/** Decode one Terrarium PNG into metres. */
async function fetchTile(
  z: number,
  x: number,
  y: number,
  signal?: AbortSignal
): Promise<Float32Array | null> {
  const res = await fetch(`${URL_BASE}/${z}/${x}/${y}.png`, { signal });
  if (!res.ok) return null;
  const blob = await res.blob();
  const bitmap = await createImageBitmap(blob);

  const canvas = new OffscreenCanvas(TILE, TILE);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('2D context unavailable for DEM decoding');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  const { data } = ctx.getImageData(0, 0, TILE, TILE);
  const out = new Float32Array(TILE * TILE);
  for (let i = 0, p = 0; i < out.length; i++, p += 4) {
    out[i] = data[p] * 256 + data[p + 1] + data[p + 2] / 256 - 32768;
  }
  return out;
}

export const terrariumProvider: DemProvider = {
  info: {
    id: 'terrarium',
    label: 'AWS Terrain Tiles',
    nativeResolution: 30,
    attribution:
      'Elevation: AWS Terrain Tiles (SRTM/NED/others, public domain and CC-BY sources)',
    covers: REGIONS.global,
  },

  async load(req: DemRequest, domain: MetricDomain): Promise<HeightGrid> {
    const { gridSize } = domain;
    const latC = domain.frame.lat0;

    // One tile pixel per grid cell, capped so a wide view does not fetch
    // hundreds of tiles.
    let zoom = zoomForResolution(latC, domain.cellSize, MAX_ZOOM);
    let range = tileRange(domain, zoom);
    while (range.count > MAX_TILES && zoom > 1) {
      zoom -= 1;
      range = tileRange(domain, zoom);
    }

    const { minX, minY, maxX, maxY } = range;
    const cols = maxX - minX + 1;
    const rows = maxY - minY + 1;

    const tiles = await Promise.all(
      Array.from({ length: cols * rows }, (_, i) => {
        const tx = minX + (i % cols);
        const ty = minY + Math.floor(i / cols);
        const n = Math.pow(2, zoom);
        // Wrap in x, clamp in y — the poles have no tiles.
        const wrappedX = ((tx % n) + n) % n;
        if (ty < 0 || ty >= n) return Promise.resolve(null);
        return fetchTile(zoom, wrappedX, ty, req.signal).catch(() => null);
      })
    );

    if (tiles.every((t) => t === null)) throw new NoCoverageError('terrarium');

    const data = new Float32Array(gridSize * gridSize);
    const scale = Math.pow(2, zoom);

    for (let j = 0; j < gridSize; j++) {
      for (let i = 0; i < gridSize; i++) {
        const [lon, lat] = gridToLonLat(domain, i, j);
        // Global pixel coordinates in the tile pyramid.
        const px = lonToTileX(lon, zoom) * TILE;
        const py = latToTileY(lat, zoom) * TILE;
        data[j * gridSize + i] = bilinearMosaic(
          tiles,
          cols,
          rows,
          minX,
          minY,
          px,
          py,
          scale
        );
      }
    }

    return {
      data,
      width: gridSize,
      height: gridSize,
      cellSize: domain.cellSize,
      origin: domain.origin,
      crs: domain.frame.crs,
      source: `AWS Terrain Tiles z${zoom}`,
    };
  },
};

function tileRange(domain: MetricDomain, zoom: number) {
  const b = domain.bbox;
  const minX = Math.floor(lonToTileX(b[0], zoom));
  const maxX = Math.floor(lonToTileX(b[2], zoom));
  // Tile y increases southward, so north maps to the smaller index.
  const minY = Math.floor(latToTileY(b[3], zoom));
  const maxY = Math.floor(latToTileY(b[1], zoom));
  const count = (maxX - minX + 1) * (maxY - minY + 1);
  return { minX, minY, maxX, maxY, count };
}

/** Bilinear sample across the tile mosaic, in global pixel coordinates. */
function bilinearMosaic(
  tiles: (Float32Array | null)[],
  cols: number,
  rows: number,
  minTX: number,
  minTY: number,
  px: number,
  py: number,
  scale: number
): number {
  const readPixel = (gx: number, gy: number): number => {
    const maxPx = scale * TILE;
    const wx = ((gx % maxPx) + maxPx) % maxPx;
    const cy = Math.min(maxPx - 1, Math.max(0, gy));
    const tx = Math.floor(wx / TILE) - minTX;
    const ty = Math.floor(cy / TILE) - minTY;
    if (tx < 0 || ty < 0 || tx >= cols || ty >= rows) return 0;
    const tile = tiles[ty * cols + tx];
    if (!tile) return 0;
    return tile[(cy - (ty + minTY) * TILE) * TILE + (wx - (tx + minTX) * TILE)];
  };

  const x0 = Math.floor(px - 0.5);
  const y0 = Math.floor(py - 0.5);
  const fx = px - 0.5 - x0;
  const fy = py - 0.5 - y0;

  const v00 = readPixel(x0, y0);
  const v10 = readPixel(x0 + 1, y0);
  const v01 = readPixel(x0, y0 + 1);
  const v11 = readPixel(x0 + 1, y0 + 1);

  return (
    v00 * (1 - fx) * (1 - fy) + v10 * fx * (1 - fy) + v01 * (1 - fx) * fy + v11 * fx * fy
  );
}
